import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Plus, MapPin, Clock, Sparkles, Lock, ListChecks,
  Settings as SettingsIcon, Shield, Heart, Compass, MessageSquare, ChevronRight, HelpCircle, Gem, Gift,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useNow } from "@/hooks/useNow";
import { useOnline } from "@/hooks/useOnline";
import { todayISO, formatLongDate, pad2 } from "@/lib/time";
import NextEventCard from "@/components/NextEventCard";
import { RedeemCodeDialog } from "@/components/RedeemCodeDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useTranslation } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import { aiUnlocked, isAdmin, aiAccess, MODE_LEVEL } from "@/lib/access";
import { toast } from "sonner";
import chroniclesLogo from "@/assets/chronicles-logo.png";

const AI_MODES = [
  { key: "assistant", icon: CalendarDays },
  { key: "strategy", icon: Compass },
  { key: "support", icon: Heart },
];

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const now = useNow(1000);
  const online = useOnline();
  const today = todayISO();
  const [redeemOpen, setRedeemOpen] = useState(false);

  const unlocked = aiUnlocked(user);

  const openAssistant = (mode) => {
    if (!online) {
      toast.error(t("home.aiOfflineToastTitle"), { description: t("home.aiOfflineToastDesc") });
      return;
    }
    if (!unlocked) {
      navigate("/palvelumme");
      return;
    }
    if (mode) {
      if (!aiAccess(user, MODE_LEVEL[mode] || "assistant").allowed) {
        navigate("/palvelumme");
        return;
      }
      navigate("/assistant", { state: { mode } });
      return;
    }
    // "Open AI Conversation": if no role is available, send to Palvelumme.
    const anyAvailable = AI_MODES.some((m) => aiAccess(user, MODE_LEVEL[m.key] || "assistant").allowed);
    if (!anyAvailable) {
      navigate("/palvelumme");
      return;
    }
    navigate("/assistant");
  };

  const time = `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;

  return (
    <div className="relative z-10 min-h-screen flex flex-col bg-background transition-colors duration-300 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-4xl px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          {/* Top-left: Settings control (always reachable) + Chronicles brand */}
          <button
            onClick={() => navigate("/settings")}
            className="flex items-center gap-2.5 rounded-full pr-2 hover:bg-accent transition btn-lift"
            data-testid="home-settings-btn"
            aria-label={t("home.account")}
            title={t("home.account")}
          >
            <span className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center shrink-0">
              <SettingsIcon className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
            </span>
            <span className="font-display text-base font-semibold tracking-tight text-foreground">
              Chronicles
            </span>
          </button>
          <div className="flex items-center gap-1.5">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <button
              onClick={() => navigate("/palvelumme")}
              className="h-8 px-2.5 sm:px-3 rounded-full hover:bg-accent flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition text-xs font-medium border border-border btn-lift"
              data-testid="home-palvelumme-link"
              aria-label={t("palvelumme.nav")}
            >
              <Gem className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("palvelumme.nav")}</span>
            </button>
            <button
              onClick={() => navigate("/onboarding")}
              className="h-8 px-2.5 sm:px-3 rounded-full hover:bg-accent flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition text-xs font-medium border border-border btn-lift"
              data-testid="home-guide-btn"
              aria-label={t("onboarding.guide")}
            >
              <HelpCircle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t("onboarding.guide")}</span>
            </button>
            {isAdmin(user) ? (
              <button
                onClick={() => navigate("/admin")}
                className="h-8 px-2.5 sm:px-3 rounded-full hover:bg-accent flex items-center gap-1.5 text-slate-600 hover:text-slate-900 transition text-xs font-medium border border-border btn-lift"
                data-testid="home-admin-link"
                aria-label={t("admin.navLink")}
              >
                <Shield className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t("admin.navLink")}</span>
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-4xl px-3 sm:px-6 py-4 sm:py-12">
        {/* Hero card — compact side-by-side on mobile, spacious on desktop */}
        <section
          className="surface-card p-4 sm:p-12 mb-3 sm:mb-6 flex flex-row items-center justify-between gap-3 sm:gap-8"
          data-testid="home-hero-card"
        >
          <div className="min-w-0">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-muted-foreground mb-1 sm:mb-3" data-testid="home-overline">
              {t("home.overline")}
            </p>
            <h1 className="font-display text-2xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.05]" data-testid="home-title">
              Chronicles
            </h1>
            <p className="mt-1.5 sm:mt-4 text-sm sm:text-lg font-light text-slate-600 max-w-md leading-snug sm:leading-relaxed" data-testid="home-subtitle">
              {t("home.tagline")}
            </p>
          </div>
          <div className="shrink-0 flex items-center justify-center">
            <img
              src={chroniclesLogo}
              alt="Chronicles logo"
              className="h-14 w-auto sm:h-40 object-contain select-none"
              draggable="false"
              data-testid="home-logo"
            />
          </div>
        </section>

        {/* Today card */}
        <section className="surface-card p-3.5 sm:p-6 mb-3 sm:mb-6" data-testid="home-clock-card">
          <div className="flex items-end justify-between gap-3 sm:gap-4">
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1 sm:mb-1.5">{t("home.localTime")}</p>
              <p className="font-mono-tab text-3xl sm:text-5xl font-semibold tracking-tight text-foreground leading-none" data-testid="home-clock">
                {time}
              </p>
            </div>
            <div className="text-right min-w-0">
              <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1 sm:mb-1.5">{t("home.today")}</p>
              <p className="font-display text-base sm:text-xl font-semibold tracking-tight text-foreground" data-testid="home-date">
                {formatLongDate(today)}
              </p>
            </div>
          </div>
        </section>

        {/* Next Event card */}
        <div className="mb-3 sm:mb-6">
          <NextEventCard />
        </div>

        {/* Main content: two columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-6 lg:gap-8">
          {/* LEFT — actions */}
          <section className="md:col-span-7 lg:col-span-8 flex flex-col" data-testid="home-actions">
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-foreground/80 mb-2.5 sm:mb-4 px-1">{t("home.actionsTitle")}</p>
            <div className="grid gap-3 content-start">
              <Button
                size="lg"
                className="btn-lift rounded-[20px] h-12 sm:h-14 text-sm sm:text-base font-medium justify-between px-5 sm:px-6"
                onClick={() => navigate(`/calendar/${today}`)}
                data-testid="home-open-today"
              >
                <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{t("home.openToday")}</span>
                <span className="text-xs opacity-70 font-mono-tab">{today}</span>
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="btn-lift rounded-[20px] h-12 sm:h-14 border-border bg-card text-foreground hover:bg-accent justify-between px-5 sm:px-6"
                onClick={() => navigate("/tasks")}
                data-testid="home-open-tasks"
              >
                <span className="inline-flex items-center gap-2"><ListChecks className="h-4 w-4" />{t("home.openTasks")}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("home.lists", { count: 4 })}</span>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3 sm:mt-auto pt-0 sm:pt-3">
              <Button
                variant="outline"
                size="lg"
                className="btn-lift rounded-[20px] h-12 sm:h-14 border-border bg-card text-foreground hover:bg-accent"
                onClick={() => navigate("/month")}
                data-testid="home-go-to-date"
              >
                <CalendarDays className="h-4 w-4 mr-1.5" />{t("home.goToDate")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="btn-lift rounded-[20px] h-12 sm:h-14 border-border bg-card text-foreground hover:bg-accent"
                onClick={() => navigate("/create")}
                data-testid="home-create-chronicle"
              >
                <Plus className="h-4 w-4 mr-1.5" />{t("home.createChronicle")}
              </Button>
            </div>

            <Button
              variant="outline"
              size="lg"
              className="btn-lift rounded-[20px] h-12 sm:h-14 border-border bg-card text-foreground hover:bg-accent justify-center mt-3"
              onClick={() => setRedeemOpen(true)}
              data-testid="home-redeem-code"
            >
              <Gift className="h-4 w-4 mr-1.5" />{t("home.redeemCode")}
            </Button>
          </section>

          {/* RIGHT — Chronicles AI */}
          <section className="md:col-span-5 lg:col-span-4 flex flex-col mt-3 sm:mt-0" data-testid="home-ai-group">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 sm:mb-3 px-1">{t("home.aiTitle")}</p>
            <div className="surface-card p-3.5 sm:p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                <span className="relative inline-flex">
                  <Sparkles className={online && unlocked ? "h-4 w-4 text-slate-900" : "h-4 w-4 text-slate-400"} />
                  {!online || !unlocked ? (
                    <span
                      className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground flex items-center justify-center ring-2 ring-card"
                      data-testid="home-assistant-lock"
                      aria-label={online ? "AI locked" : "AI offline"}
                    >
                      <Lock className="h-2 w-2" strokeWidth={3} />
                    </span>
                  ) : null}
                </span>
                <span className="font-display text-base font-semibold text-foreground">{t("home.aiTitle")}</span>
              </div>

              <div className="grid gap-1.5">
                {AI_MODES.map(({ key, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => openAssistant(key)}
                    className="btn-lift w-full flex items-center gap-2.5 rounded-[16px] border border-border bg-card hover:bg-accent px-3.5 py-2.5 text-left transition"
                    data-testid={`home-ai-${key}`}
                  >
                    <Icon className={online && unlocked ? "h-4 w-4 text-slate-700 shrink-0" : "h-4 w-4 text-slate-400 shrink-0"} />
                    <span className="flex-1 text-sm text-foreground truncate">{t(`assistant.modes.${key}.label`)}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                ))}
              </div>

              <Button
                size="lg"
                className="btn-lift rounded-[20px] h-12 w-full mt-3 sm:mt-auto justify-between px-5"
                onClick={() => openAssistant()}
                data-testid="home-open-assistant"
                data-ai-offline={!online}
                data-ai-locked={online && !unlocked}
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <MessageSquare className="h-4 w-4 shrink-0" />
                  <span className="truncate">{!online ? t("home.aiOffline") : !unlocked ? t("home.aiLockedLabel") : t("home.openChat")}</span>
                </span>
                <span className="hidden lg:inline text-[10px] uppercase tracking-[0.2em] opacity-70 shrink-0">
                  {!online ? t("home.locked") : !unlocked ? t("home.upgrade") : t("home.beta")}
                </span>
              </Button>

              {online && !unlocked ? (
                <button
                  className="text-xs text-slate-500 hover:text-slate-900 underline text-center w-full mt-3"
                  onClick={() => navigate("/palvelumme")}
                  data-testid="home-have-code"
                >
                  {t("home.seePlans")}
                </button>
              ) : null}
            </div>
          </section>
        </div>

        {/* Security footer */}
        <footer className="py-5 sm:py-10 mt-2 sm:mt-6 text-center" data-testid="home-security-footer">
          <p className="text-xs text-muted-foreground tracking-[0.14em]" data-testid="home-offline-note">
            {online ? t("home.statusOnline") : t("home.statusOffline")}
          </p>
        </footer>
      </main>

      <RedeemCodeDialog open={redeemOpen} onOpenChange={setRedeemOpen} />
    </div>
  );
}
